/**
 * Product Widgets Orchestrator
 * Main system for managing product page widgets
 *
 * This orchestrator:
 * - Loads bundle configuration
 * - Manages widget lifecycle (init, render, destroy)
 * - Provides event bus for widget communication
 * - Implements lazy loading via Intersection Observer
 * - Provides shared utilities (formatPrice, etc.)
 *
 * @version 1.0.0
 */

(function(window) {
  'use strict';

  /**
   * Main ProductWidgets Orchestrator Class
   * Manages all widget instances on the product page
   */
  class ProductWidgetsOrchestrator {
    /**
     * @param {Object} options - Configuration options
     * @param {Object} options.productData - Current product data from Liquid
     * @param {Object} options.settings - Widget settings from theme editor
     */
    constructor(options = {}) {
      this.productData = options.productData || {};
      this.settings = options.settings || {};
      this.bundleConfig = null;
      this.widgets = new Map();
      this.eventHandlers = new Map();
      this.intersectionObserver = null;
      this.initialized = false;
      this.bundleConfigReady = false; // NEW: Track bundleConfig separately from initialized

      // Bind methods
      this.emit = this.emit.bind(this);
      this.on = this.on.bind(this);
      this.off = this.off.bind(this);
    }

    /**
     * Wait for orchestrator to be ready
     * Returns a promise that resolves when the global orchestrator is initialized
     * This eliminates the need for setTimeout retry loops in widgets
     * @returns {Promise<ProductWidgetsOrchestrator>}
     */
    static waitForReady() {
      return new Promise((resolve) => {
        // Check if orchestrator already exists and is initialized
        if (window.productWidgetsOrchestrator?.initialized) {
          resolve(window.productWidgetsOrchestrator);
          return;
        }

        // Check if orchestrator exists but not initialized yet
        if (window.productWidgetsOrchestrator) {
          window.productWidgetsOrchestrator.on('orchestrator:ready', () => {
            resolve(window.productWidgetsOrchestrator);
          });
          return;
        }

        // Orchestrator doesn't exist yet, wait for it to be created
        const checkInterval = setInterval(() => {
          if (window.productWidgetsOrchestrator) {
            clearInterval(checkInterval);

            if (window.productWidgetsOrchestrator.initialized) {
              resolve(window.productWidgetsOrchestrator);
            } else {
              window.productWidgetsOrchestrator.on('orchestrator:ready', () => {
                resolve(window.productWidgetsOrchestrator);
              });
            }
          }
        }, 50); // Check every 50ms (less CPU intensive than per-widget polling)
      });
    }

    /**
     * Wait for bundleConfig to be loaded
     * Returns a promise that resolves with bundleConfig when ready
     * @returns {Promise<Object>} Resolves with bundleConfig when loaded
     */
    waitForBundleConfig() {
      return new Promise((resolve) => {
        if (this.bundleConfigReady && this.bundleConfig) {
          resolve(this.bundleConfig);
        } else {
          this.once('bundleConfig:ready', (event) => {
            resolve(event.config);
          });
        }
      });
    }

    /**
     * Wait for a custom condition before proceeding
     * Useful for waiting on specific dependencies
     * @param {Function} condition - Function that returns true when ready
     * @param {Number} timeout - Max wait time in ms (default 5000)
     * @returns {Promise<void>}
     */
    waitUntil(condition, timeout = 5000) {
      return new Promise((resolve, reject) => {
        const startTime = Date.now();

        const check = () => {
          if (condition()) {
            resolve();
          } else if (Date.now() - startTime > timeout) {
            reject(new Error('waitUntil timeout'));
          } else {
            setTimeout(check, 50);
          }
        };

        check();
      });
    }

    /**
     * Initialize the orchestrator
     * Loads bundle config and sets up observers
     * @returns {Promise<void>}
     */
    async init() {
      if (this.initialized) {
        console.warn('[ProductWidgets] Already initialized');
        return;
      }

      try {
        // Load bundle configuration FIRST
        this.bundleConfig = await this.loadBundleConfig();
        this.bundleConfigReady = true; // NEW: Signal config loaded
        this.emit('bundleConfig:ready', { config: this.bundleConfig }); // NEW: Event for waiting widgets

        // Initialize bundleAdapter with config
        if (window.bundleAdapter && this.bundleConfig) {
          await window.bundleAdapter.initialize(this.bundleConfig);
        }

        // Setup intersection observer for lazy loading
        this.setupIntersectionObserver();

        this.initialized = true;
        this.emit('orchestrator:ready', { orchestrator: this });
      } catch (error) {
        console.error('[ProductWidgets] Initialization failed:', error);
        this.emit('orchestrator:error', { error });
      }
    }

    /**
     * Load bundle configuration from JSON file
     * @returns {Promise<Object>} Bundle configuration object
     */
    async loadBundleConfig() {
      try {
        const configUrl = this.settings.bundleConfigUrl || '/assets/bundle-config.json';
        const response = await fetch(configUrl);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const config = await response.json();
        return config;
      } catch (error) {
        console.error('[ProductWidgets] Failed to load bundle config:', error);
        // Return minimal fallback config
        return {
          santaLetterFree: true,
          tiers: {},
          addons: {}
        };
      }
    }

    /**
     * Setup Intersection Observer for lazy loading widgets
     * Widgets marked with lazyLoad: true will only render when visible
     */
    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        console.warn('[ProductWidgets] IntersectionObserver not supported, lazy loading disabled');
        return;
      }

      const options = {
        root: null,
        rootMargin: '300px', // Start loading 300px before widget enters viewport (smoother UX)
        threshold: 0.01
      };

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const widgetId = entry.target.dataset.pwWidgetId;
            const widget = this.widgets.get(widgetId);

            if (widget && widget.lazyLoad && !widget.rendered) {
              this.renderWidget(widgetId);
              this.intersectionObserver.unobserve(entry.target);
            }
          }
        });
      }, options);
    }

    /**
     * Register a new widget with the orchestrator
     * @param {string} id - Unique widget identifier
     * @param {BaseWidget} widgetInstance - Widget instance
     * @returns {void}
     */
    registerWidget(id, widgetInstance) {
      if (this.widgets.has(id)) {
        console.warn(`[ProductWidgets] Widget "${id}" already registered`);
        return;
      }

      this.widgets.set(id, widgetInstance);
      this.emit('widget:registered', { id, widget: widgetInstance });

      // CRITICAL FIX: If widget is lazy-loaded and orchestrator is initialized,
      // observe it immediately (handles widgets that register after renderAllWidgets)
      if (widgetInstance.lazyLoad && this.initialized && this.intersectionObserver && widgetInstance.container) {
        this.intersectionObserver.observe(widgetInstance.container);
      }
    }

    /**
     * Render a specific widget by ID
     * @param {string} id - Widget identifier
     * @returns {Promise<void>}
     */
    async renderWidget(id) {
      const widget = this.widgets.get(id);

      if (!widget) {
        console.error(`[ProductWidgets] Widget "${id}" not found`);
        return;
      }

      if (widget.rendered) {
        console.warn(`[ProductWidgets] Widget "${id}" already rendered`);
        return;
      }

      const maxRetries = 3;
      let attempt = 0;

      while (attempt < maxRetries) {
        try {
          // Widget.init() now calls waitForDependencies() internally
          await widget.init();
          await widget.render();
          widget.rendered = true;

          // Add loaded class to trigger skeleton fade-out and content fade-in
          if (widget.container) {
            widget.container.classList.add('pw-widget--loaded');
            widget.container.classList.remove('pw-skeleton-widget');
          }

          this.emit('widget:rendered', { id, widget });
          return; // Success - exit retry loop
        } catch (error) {
          attempt++;

          if (attempt < maxRetries) {
            console.warn(`[ProductWidgets] Widget "${id}" render attempt ${attempt} failed, retrying...`, error);
            await new Promise(resolve => setTimeout(resolve, 200 * attempt)); // Exponential backoff: 200ms, 400ms, 600ms
          } else {
            console.error(`[ProductWidgets] Failed to render widget "${id}" after ${maxRetries} attempts:`, error);

            // Show error state in widget container
            if (widget.container) {
              widget.container.innerHTML = `
                <div class="pw-widget-error" style="padding: 1rem; text-align: center; color: rgb(var(--color-base-text) / 0.6); font-size: 0.875rem;">
                  <p>Unable to load widget</p>
                </div>
              `;
              widget.container.classList.add('pw-widget--error');
              widget.container.classList.remove('pw-skeleton-widget');
            }

            this.emit('widget:error', { id, widget, error });
            throw error;
          }
        }
      }
    }

    /**
     * Render all registered widgets
     * Respects lazyLoad setting - lazy widgets are only observed, not rendered
     * Uses requestAnimationFrame to batch DOM updates into a single frame
     * @returns {Promise<void>}
     */
    async renderAllWidgets() {
      return new Promise((resolve) => {
        // Batch all rendering into a single animation frame to reduce layout thrashing
        requestAnimationFrame(async () => {
          const renderPromises = [];

          for (const [id, widget] of this.widgets) {
            if (widget.lazyLoad && this.intersectionObserver) {
              // Don't render, just observe for lazy loading
              if (widget.container) {
                this.intersectionObserver.observe(widget.container);
              }
            } else {
              // Render immediately
              renderPromises.push(this.renderWidget(id));
            }
          }

          await Promise.all(renderPromises);

          // CRITICAL: Unlock scroll position now that widgets are loaded
          // This prevents page jumping during initial load
          if (typeof window.unlockPageScroll === 'function') {
            // Small delay to ensure all DOM updates are complete
            requestAnimationFrame(() => {
              window.unlockPageScroll();
            });
          }

          resolve();
        });
      });
    }

    /**
     * Destroy a specific widget by ID
     * @param {string} id - Widget identifier
     * @returns {void}
     */
    destroyWidget(id) {
      const widget = this.widgets.get(id);

      if (!widget) {
        console.warn(`[ProductWidgets] Widget "${id}" not found`);
        return;
      }

      try {
        if (widget.container && this.intersectionObserver) {
          this.intersectionObserver.unobserve(widget.container);
        }

        widget.destroy();
        this.widgets.delete(id);

        this.emit('widget:destroyed', { id });
      } catch (error) {
        console.error(`[ProductWidgets] Failed to destroy widget "${id}":`, error);
      }
    }

    /**
     * Destroy all widgets and cleanup
     * @returns {void}
     */
    destroyAll() {
      for (const id of this.widgets.keys()) {
        this.destroyWidget(id);
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      this.eventHandlers.clear();
      this.initialized = false;
    }

    /**
     * Event Bus: Emit an event to all listeners
     * @param {string} eventName - Name of the event
     * @param {*} data - Data to pass to event handlers
     * @returns {void}
     */
    emit(eventName, data) {
      const handlers = this.eventHandlers.get(eventName) || [];

      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`[ProductWidgets] Event handler error for "${eventName}":`, error);
        }
      });
    }

    /**
     * Event Bus: Subscribe to an event
     * @param {string} eventName - Name of the event
     * @param {Function} handler - Event handler function
     * @returns {Function} Unsubscribe function
     */
    on(eventName, handler) {
      if (typeof handler !== 'function') {
        console.error('[ProductWidgets] Event handler must be a function');
        return () => {};
      }

      if (!this.eventHandlers.has(eventName)) {
        this.eventHandlers.set(eventName, []);
      }

      this.eventHandlers.get(eventName).push(handler);

      // Return unsubscribe function
      return () => this.off(eventName, handler);
    }

    /**
     * Event Bus: Unsubscribe from an event
     * @param {string} eventName - Name of the event
     * @param {Function} handler - Event handler function to remove
     * @returns {void}
     */
    off(eventName, handler) {
      const handlers = this.eventHandlers.get(eventName);

      if (!handlers) {
        return;
      }

      const index = handlers.indexOf(handler);
      if (index !== -1) {
        handlers.splice(index, 1);
      }

      // Clean up empty handler arrays
      if (handlers.length === 0) {
        this.eventHandlers.delete(eventName);
      }
    }

    /**
     * Get widget instance by ID
     * @param {string} id - Widget identifier
     * @returns {BaseWidget|null}
     */
    getWidget(id) {
      return this.widgets.get(id) || null;
    }

    /**
     * Check if product is eligible for a specific addon
     * @param {Object} addon - Addon configuration from bundle-config.json
     * @returns {boolean}
     */
    isAddonEligible(addon) {
      if (!addon || !this.productData.tags) {
        return false;
      }

      const productTags = this.productData.tags.map(tag => tag.trim());

      // Check eligibility tags (must have at least one)
      if (addon.eligibilityTags && addon.eligibilityTags.length > 0) {
        const hasEligibleTag = addon.eligibilityTags.some(tag =>
          productTags.includes(tag)
        );

        if (!hasEligibleTag) {
          return false;
        }
      }

      // Check exclude tags (must not have any)
      if (addon.excludeTags && addon.excludeTags.length > 0) {
        const hasExcludedTag = addon.excludeTags.some(tag =>
          productTags.includes(tag)
        );

        if (hasExcludedTag) {
          return false;
        }
      }

      // Check specific products
      if (addon.specificProducts && addon.specificProducts.length > 0) {
        if (!addon.specificProducts.includes(this.productData.handle)) {
          return false;
        }
      }

      return true;
    }

    /**
     * Get all eligible addons for current product
     * @param {string} category - Optional category filter (e.g., 'performance', 'personalization')
     * @returns {Array<Object>}
     */
    getEligibleAddons(category = null) {
      if (!this.bundleConfig || !this.bundleConfig.addons) {
        return [];
      }

      let addonsToCheck = [];

      if (category) {
        addonsToCheck = this.bundleConfig.addons[category] || [];
      } else {
        // Get all addons from all categories
        addonsToCheck = Object.values(this.bundleConfig.addons).flat();
      }

      return addonsToCheck.filter(addon => this.isAddonEligible(addon));
    }

    /**
     * Format price for display
     * @param {number} cents - Price in cents
     * @param {string} currency - Currency code (default: 'GBP')
     * @returns {string} Formatted price string
     */
    formatPrice(cents, currency = 'GBP') {
      if (typeof cents !== 'number') {
        return '£0.00';
      }

      const amount = cents / 100;

      try {
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: currency
        }).format(amount);
      } catch (error) {
        // Fallback if Intl not supported
        return `£${amount.toFixed(2)}`;
      }
    }

    /**
     * Calculate discount for a given spend amount
     * @param {number} spendAmount - Total addon spend in currency units
     * @returns {Object} { tierKey, tierName, discountPercent, discountAmount }
     */
    calculateDiscount(spendAmount) {
      if (!this.bundleConfig || !this.bundleConfig.tiers) {
        return { tierKey: 'basic', tierName: 'Basic', discountPercent: 0, discountAmount: 0 };
      }

      const tiers = this.bundleConfig.tiers;
      let applicableTier = tiers.basic || { name: 'Basic', discount: '0%', spendThreshold: 0 };

      // Find highest applicable tier
      Object.entries(tiers).forEach(([key, tier]) => {
        if (tier.spendThreshold && spendAmount >= tier.spendThreshold) {
          if (tier.spendThreshold > applicableTier.spendThreshold) {
            applicableTier = { ...tier, key };
          }
        }
      });

      const discountPercent = parseInt(applicableTier.discount) || 0;
      const discountAmount = (spendAmount * discountPercent) / 100;

      return {
        tierKey: applicableTier.key || 'basic',
        tierName: applicableTier.name,
        discountPercent,
        discountAmount
      };
    }

    /**
     * Debounce utility
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in milliseconds
     * @returns {Function}
     */
    debounce(func, wait = 300) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    /**
     * Throttle utility
     * @param {Function} func - Function to throttle
     * @param {number} limit - Time limit in milliseconds
     * @returns {Function}
     */
    throttle(func, limit = 300) {
      let inThrottle;
      return function(...args) {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    }
  }

  /**
   * Base Widget Class
   * All widgets should extend this class or implement its interface
   */
  class BaseWidget {
    /**
     * @param {ProductWidgetsOrchestrator} manager - Reference to orchestrator
     * @param {HTMLElement} container - DOM element to render into
     * @param {Object} config - Widget-specific configuration
     */
    constructor(manager, container, config = {}) {
      if (!manager) {
        throw new Error('BaseWidget requires a manager instance');
      }

      if (!container) {
        throw new Error('BaseWidget requires a container element');
      }

      this.manager = manager;
      this.container = container;
      this.config = config;
      this.productData = manager.productData;
      this.bundleConfig = manager.bundleConfig;
      this.lazyLoad = config.lazyLoad || false;
      this.requiresBundleConfig = config.requiresBundleConfig || false; // NEW: Track dependency on bundleConfig
      this.rendered = false;

      // Set widget ID on container for lazy loading
      if (this.config.id) {
        this.container.dataset.pwWidgetId = this.config.id;
      }
    }

    /**
     * Wait for dependencies before initializing
     * Subclasses can override to add custom dependency checks
     * @returns {Promise<void>}
     */
    async waitForDependencies() {
      // If widget needs bundleConfig, wait for it
      if (this.requiresBundleConfig && !this.manager.bundleConfigReady) {
        await this.manager.waitForBundleConfig();
        // Update bundleConfig reference after it's loaded
        this.bundleConfig = this.manager.bundleConfig;
      }
    }

    /**
     * Initialize widget (fetch data, setup, etc.)
     * Override in subclass for custom init logic
     * @returns {Promise<void>}
     */
    async init() {
      // Wait for dependencies first (bundleConfig if needed)
      await this.waitForDependencies();
      // Subclasses override this for custom init logic
    }

    /**
     * Render widget to container
     * Override in subclass - REQUIRED
     * @returns {void}
     */
    render() {
      throw new Error('Widget must implement render() method');
    }

    /**
     * Update widget with new data
     * Override in subclass if needed
     * @param {Object} data - New data
     * @returns {void}
     */
    update(data) {
    }

    /**
     * Show widget (for lazy loaded or hidden widgets)
     * Override in subclass if needed
     * @returns {void}
     */
    show() {
      if (this.container) {
        this.container.style.display = '';
        this.container.setAttribute('aria-hidden', 'false');
      }
    }

    /**
     * Hide widget
     * Override in subclass if needed
     * @returns {void}
     */
    hide() {
      if (this.container) {
        this.container.style.display = 'none';
        this.container.setAttribute('aria-hidden', 'true');
      }
    }

    /**
     * Set widget content while preserving skeleton loading
     * This method should be used instead of directly setting innerHTML
     * @param {string} html - HTML content to render
     * @returns {void}
     */
    setContent(html) {
      if (!this.container) return;

      // Clear ALL existing content to prevent duplicates
      // This handles both skeleton content and any previous renders
      this.container.innerHTML = '';

      // Insert new content
      this.container.insertAdjacentHTML('beforeend', html);

      // Mark widget as loaded (makes content visible and removes skeleton classes)
      this.container.classList.add('pw-widget--loaded');
      this.container.classList.remove('pw-skeleton-widget');
    }

    /**
     * Cleanup and remove widget
     * Override in subclass to add custom cleanup
     * @returns {void}
     */
    destroy() {
      if (this.container) {
        this.container.innerHTML = '';
      }
      this.rendered = false;
    }

    /**
     * Emit event through orchestrator event bus
     * @param {string} eventName - Event name
     * @param {*} data - Event data
     * @returns {void}
     */
    emit(eventName, data) {
      this.manager.emit(eventName, { ...data, widgetId: this.config.id });
    }

    /**
     * Subscribe to event through orchestrator event bus
     * @param {string} eventName - Event name
     * @param {Function} handler - Event handler
     * @returns {Function} Unsubscribe function
     */
    on(eventName, handler) {
      return this.manager.on(eventName, handler);
    }
  }

  // Expose to global scope
  // Preserve any widgets that may have already been registered
  const existingWidgets = (window.ProductWidgets && window.ProductWidgets.widgets) || {};

  window.ProductWidgets = {
    Orchestrator: ProductWidgetsOrchestrator,
    BaseWidget: BaseWidget,
    widgets: existingWidgets, // Preserve existing widget classes
    version: '1.0.0'
  };


})(window);
